///
/// Copyright (c) 2017-present, Siklu Ltd.
/// All rights reserved.
///

import { Component, ElementRef, HostListener, OnDestroy, OnInit } from '@angular/core';
import { GtthReportsService } from '../../../../services/project/reports.service';
import { GtthHistoryService } from '../../../logical-topology/engine/history-service';
import { Subscription } from 'rxjs';

import { AbstractInteractiveWindow } from '../../abstract-interactive-window';
import { ProjectInteractiveWindowsPopupService } from '../../project-interactive-windows-popup.service';
import { GtthExportService } from '../../../../services/project/export.service';
import { PARAM_BBOX } from '../../../../shared/constants/url-params.constants';
import { ActivatedRoute } from '@angular/router';
import { GtthProjectInfoViewService } from '../../../../layouts/navbar/project-info-view.service';
import { MatDialogRef } from '@angular/material/dialog';
import {
    ExtendedNodeConnectedDataDTO,
    PotentiallyProblematicBuDeviceDTO,
    TopologyValidityDTO
} from '../../../../generated/shared-types.model';

@Component({
    selector: 'winde-report-connected-sites',
    templateUrl: './report-connected-sites.component.html',
    styleUrls: ['../../project-windows.scss'],
    standalone: false
})
export class GtthReportConnectedSitesComponent extends AbstractInteractiveWindow implements OnInit, OnDestroy {
    public stationConn: ExtendedNodeConnectedDataDTO[];
    public unconnectedNoLosStations: ExtendedNodeConnectedDataDTO[];
    public unconnectedNoProdMatchingStations: ExtendedNodeConnectedDataDTO[];
    public stationDisabled: ExtendedNodeConnectedDataDTO[];
    public stationBuOnly: ExtendedNodeConnectedDataDTO[];
    public layers: Record<string, string>;
    public collapseConn = true;
    public collapseNoLOS = false;
    public collapseNoMatchingProd = false;
    public collapseDisabled = true;
    public collapseBuOnly = true;
    public collapseProblematicBUs = false;
    public refresh: boolean;
    public disableExportButton: boolean;
    public problematicBUs: PotentiallyProblematicBuDeviceDTO[];
    private layersChangedSubscriber: Subscription;

    constructor(
        private readonly elementRef: ElementRef,
        public dialogRef: MatDialogRef<GtthReportConnectedSitesComponent>,
        private readonly reportsService: GtthReportsService,
        private readonly historyService: GtthHistoryService,
        private readonly exportService: GtthExportService,
        private readonly projectInfoService: GtthProjectInfoViewService
    ) {
        super(elementRef);
        this.layersChangedSubscriber = projectInfoService.layerChangedSubscription().subscribe((value) => {
            this.refreshReport(value);
            this.getProjectLayers();
        });
    }

    ngOnInit() {
        this.getProjectLayers();
        this.refreshReport(this.projectInfoService.activeLayer);
    }

    ngOnDestroy() {
        if (this.layersChangedSubscriber) {
            this.layersChangedSubscriber.unsubscribe();
            this.layersChangedSubscriber = null;
        }
    }

    closeModal() {
        this.dialogRef.close('cancel');
    }

    exportToExcel() {
        this.exportService.exportConnectedSitesReport(this.projectInfoService.activeLayer);
    }

    refreshReport(layerIndex = -1) {
        this.refresh = true;
        this.stationConn = [];
        this.unconnectedNoLosStations = [];
        this.unconnectedNoProdMatchingStations = [];
        this.stationDisabled = [];
        this.stationBuOnly = [];

        const dataFromCache = this.reportsService.getTopologyValidityDtoFromCache();
        if (dataFromCache) {
            this.sortReportData(dataFromCache, layerIndex);
            this.reportsService.setTopologyValidityToCache(undefined);
        } else {
            this.reportsService.validateTopologyAfterDesign().subscribe((reportData: TopologyValidityDTO) => {
                this.sortReportData(reportData, layerIndex);
            });
        }
    }

    @HostListener('document:keydown', ['$event'])
    onKeydown(event: KeyboardEvent) {
        if (event.key === 'Escape' && !this.historyService.hasSearchMarkers()) {
            this.closeModal();
        }
    }

    designRequired(): boolean {
        return true;
    }

    isRestorable(): boolean {
        return false;
    }

    private getProjectLayers() {
        if (this.projectInfoService.layersOptions?.length) {
            this.layers = this.projectInfoService.getLayersAsKeyValueObj();
        }
    }

    private sortReportData(topologyValidityDto: TopologyValidityDTO, layerIndex) {
        this.problematicBUs = topologyValidityDto.problematicBuDevices;

        topologyValidityDto.connectedSitesReport.forEach((nodeData) => {
            if (nodeData.buOnly && (layerIndex === -1 || nodeData.layerIndex === layerIndex)) {
                this.stationBuOnly.push(nodeData);
            } else if (nodeData.isConn && nodeData.enabled && (layerIndex === -1 || nodeData.layerIndex === layerIndex)) {
                this.stationConn.push(nodeData);
            } else if (!nodeData.enabled && (layerIndex === -1 || nodeData.layerIndex === layerIndex)) {
                this.stationDisabled.push(nodeData);
            } else if (layerIndex === -1 || nodeData.layerIndex === layerIndex) {
                const station = this.historyService.logicalTopology.getStationByName(nodeData.nodeid);
                if (!station.linkIds?.size) {
                    this.unconnectedNoLosStations.push(nodeData);
                } else {
                    this.unconnectedNoProdMatchingStations.push(nodeData);
                }
            }
        });
        this.disableExportButton =
            !this.unconnectedNoProdMatchingStations?.length &&
            !this.unconnectedNoLosStations?.length &&
            !this.stationBuOnly?.length &&
            !this.stationDisabled?.length &&
            !this.stationConn?.length;

        this.refresh = false;
    }
}

@Component({
    selector: 'winde-report-connected-sites-popup',
    template: '',
    standalone: false
})
export class GtthReportConnectedSitesPopupComponent implements OnInit {
    constructor(
        private readonly interactiveWindowsPopupService: ProjectInteractiveWindowsPopupService,
        private readonly activatedRoute: ActivatedRoute
    ) {}

    ngOnInit() {
        let bboxParams;
        if (this.activatedRoute.snapshot.queryParamMap.has(PARAM_BBOX)) {
            bboxParams = this.activatedRoute.snapshot.queryParamMap
                .get(PARAM_BBOX)
                .split(',')
                .map((value) => parseFloat(value));
        }
        const routerPath = this.activatedRoute.routeConfig.path;
        this.interactiveWindowsPopupService.openInteractiveWindow(
            GtthReportConnectedSitesComponent,
            ProjectInteractiveWindowsPopupService.REP_CONNECTED_SITES,
            routerPath,
            bboxParams
        );
    }
}
